
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package } from 'lucide-react';
import { useUserOrders } from '@/hooks/useUserOrders';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const OrderHistoryTab = () => {
  const { orders, isLoading } = useUserOrders();

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
      switch (status?.toLowerCase()) {
        case 'shipped':
        case 'delivered':
          return 'default';
        case 'pending':
        case 'processing':
          return 'secondary';
        case 'cancelled':
          return 'destructive';
        default:
          return 'outline';
      }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Order History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        )}
        {!isLoading && orders.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-10 w-10 mx-auto mb-4" />
            <p className="font-semibold">No orders found</p>
            <p>You haven't placed any orders yet.</p>
          </div>
        )}
        {!isLoading && orders.length > 0 && (
          <Accordion type="single" collapsible className="w-full">
            {orders.map((order) => (
              <AccordionItem value={order.id} key={order.id}>
                <AccordionTrigger>
                  <div className="flex justify-between w-full pr-4 items-center">
                    <div className="text-left">
                      <p className="font-semibold">Order #{order.order_number}</p>
                      <p className="text-sm text-muted-foreground">{format(new Date(order.created_at), 'dd MMMM, yyyy')}</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <Badge variant={getStatusVariant(order.status)} className="capitalize">{order.status}</Badge>
                        <p className="font-bold text-lg">${Number(order.total_amount).toFixed(2)}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="p-4 bg-muted/50 rounded-md">
                    <h4 className="font-semibold mb-2">Order Details</h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <p><span className="font-medium text-muted-foreground">Payment Status:</span> <span className="capitalize">{order.payment_status}</span></p>
                      <p><span className="font-medium text-muted-foreground">Payment Method:</span> <span className="capitalize">{order.payment_method}</span></p>
                    </div>
                    <h4 className="font-semibold mt-4 mb-2">Items ({order.order_items.length})</h4>
                    <div className="space-y-2 border-t pt-2">
                      {order.order_items.map((item: any) => (
                          <div key={item.id} className="flex justify-between items-center text-sm">
                              <p>{item.products.name} (x{item.quantity})</p>
                              <p>${(item.price * item.quantity).toFixed(2)}</p>
                          </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderHistoryTab;
